import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import Spinner from "react-bootstrap/Spinner";
import { toast, ToastContainer } from "react-toastify";
import config from "../../Config";
import PayrollMenu from "./PayrollMenu";

const DEFAULT_SCHOOL_ID = 1;

function ManagePayRateDetails() {
  const [AccessKey] = useState(localStorage.getItem("AccessKey"));
  const [UserID] = useState(localStorage.getItem("ID"));
  const [MemberType] = useState(localStorage.getItem("MemberType"));

  const [loadingMaster, setLoadingMaster] = useState(true);
  const [loadingRate, setLoadingRate] = useState(false);
  const [saving, setSaving] = useState(false);

  const [payRates, setPayRates] = useState([]);
  const [salaryTypes, setSalaryTypes] = useState([]);
  const [selectedPayRateId, setSelectedPayRateId] = useState(0);
  const [selectedPayRate, setSelectedPayRate] = useState(null);
  const [amounts, setAmounts] = useState({});

  const initialIdFromQuery = useMemo(() => {
    try {
      const id = Number(new URLSearchParams(window.location.search).get("id"));
      return Number.isFinite(id) && id > 0 ? id : 0;
    } catch {
      return 0;
    }
  }, []);

  useEffect(() => {
    document.title = "Manage Pay Rate Details";
    const data = { AccessKey, UserID, MemberType };

    Promise.all([
      axios({
        method: "post",
        url: config.base_url + "Payroll/GetPayRatesList",
        headers: { "Content-Type": "application/json" },
        data,
      }),
      axios({
        method: "post",
        url: config.base_url + "SalaryType/GetSalaryTypes",
        headers: { "Content-Type": "application/json" },
        data: {
          ...data,
          pageNo: 1,
          pageSize: 5000,
          SchoolID: DEFAULT_SCHOOL_ID,
        },
      }),
    ])
      .then(([ratesRes, typesRes]) => {
        const loadedRates =
          ratesRes.data?.status_code === 1 && Array.isArray(ratesRes.data.ListofRecords)
            ? ratesRes.data.ListofRecords
            : [];
        const loadedTypes =
          typesRes.data?.status_code === 1 && Array.isArray(typesRes.data.ListofRecords)
            ? typesRes.data.ListofRecords
                .filter((x) => x && x.Is_Deleted !== true && x.Is_Active !== false)
                .sort((a, b) => (a.SortOrder || 0) - (b.SortOrder || 0))
            : [];

        setPayRates(loadedRates);
        setSalaryTypes(loadedTypes);

        if (loadedRates.length > 0) {
          const pickId =
            initialIdFromQuery > 0 && loadedRates.some((r) => r.ID === initialIdFromQuery)
              ? initialIdFromQuery
              : loadedRates[0].ID;
          setSelectedPayRateId(pickId);
        }
      })
      .catch(() => {
        toast.error("Failed to load pay-rate details master data.", {
          position: toast.POSITION.TOP_RIGHT,
        });
      })
      .finally(() => setLoadingMaster(false));
  }, [AccessKey, UserID, MemberType, initialIdFromQuery]);

  useEffect(() => {
    if (!selectedPayRateId) return;
    setLoadingRate(true);
    axios({
      method: "post",
      url: config.base_url + "Payroll/GetPayRateByID",
      headers: { "Content-Type": "application/json" },
      data: {
        AccessKey,
        UserID,
        MemberType,
        ID: selectedPayRateId,
      },
    })
      .then((res) => {
        if (res.data?.status_code !== 1 || !res.data.PayRate) {
          toast.error("Selected pay rate could not be loaded.", {
            position: toast.POSITION.TOP_RIGHT,
          });
          return;
        }
        const pr = res.data.PayRate;
        setSelectedPayRate(pr);

        const mapped = {};
        (salaryTypes || []).forEach((st) => {
          mapped[st.ID] = "";
        });
        (pr.Details || []).forEach((d) => {
          if (d?.SalaryType_ID) mapped[d.SalaryType_ID] = d.Amount;
        });
        setAmounts(mapped);
      })
      .catch(() => {
        toast.error("Network error while loading pay rate details.", {
          position: toast.POSITION.TOP_RIGHT,
        });
      })
      .finally(() => setLoadingRate(false));
  }, [selectedPayRateId, AccessKey, UserID, MemberType, salaryTypes]);

  const onAmountChange = (salaryTypeId, value) => {
    setAmounts((prev) => ({ ...prev, [salaryTypeId]: value }));
  };

  const saveDetails = () => {
    if (!selectedPayRate) return;
    setSaving(true);

    const payload = {
      AccessKey,
      UserID,
      MemberType,
      ID: selectedPayRate.ID,
      Category: selectedPayRate.Category,
      PayScale: selectedPayRate.PayScale,
      EffectiveFrom: selectedPayRate.EffectiveFrom,
      School_ID: selectedPayRate.School_ID || DEFAULT_SCHOOL_ID,
      Is_Active: selectedPayRate.Is_Active !== false,
      Is_Deleted: selectedPayRate.Is_Deleted === true,
      Details: salaryTypes.map((st) => ({
        SalaryType_ID: st.ID,
        Amount: parseFloat(amounts[st.ID]) || 0,
      })),
    };

    axios({
      method: "post",
      url: config.base_url + "Payroll/SavePayRate",
      headers: { "Content-Type": "application/json" },
      data: payload,
    })
      .then((res) => {
        if (res.data?.status_code === 1) {
          toast.success("Pay rate detail updated successfully.", {
            position: toast.POSITION.TOP_RIGHT,
          });
        } else {
          toast.error(res.data?.status_message || "Failed to update details.", {
            position: toast.POSITION.TOP_RIGHT,
          });
        }
      })
      .catch(() => {
        toast.error("Network error while saving details.", {
          position: toast.POSITION.TOP_RIGHT,
        });
      })
      .finally(() => setSaving(false));
  };

  return (
    <>
      <PayrollMenu />
      <ToastContainer />
      <div className="page-head">
        <div className="container">
          <div className="row align-items-center">
            <div className="col-md-6">
              <nav aria-label="breadcrumb">
                <ol className="breadcrumb">
                  <li className="breadcrumb-item">
                    <Link to="/">Dashboard</Link>
                  </li>
                  <li className="breadcrumb-item">
                    <Link to="/PayRoll/PayRollDashboard">PayRoll</Link>
                  </li>
                  <li className="breadcrumb-item">
                    <Link to="/Payroll/ManagePayRates">Pay Rates</Link>
                  </li>
                  <li className="breadcrumb-item active" aria-current="page">
                    Manage Pay Rate Details
                  </li>
                </ol>
              </nav>
              <div className="page-title">Manage Pay Rate Details</div>
            </div>
          </div>
        </div>
      </div>

      <div className="page-content">
        <div className="container">
          <div className="card">
            <div className="card-body">
              {loadingMaster ? (
                <div className="text-center py-4">
                  <Spinner animation="border" role="status" />
                </div>
              ) : (
                <>
                  <div className="row align-items-end">
                    <div className="col-md-6">
                      <div className="form-group mb-3">
                        <label>Select Pay Rate</label>
                        <select
                          className="form-select"
                          value={selectedPayRateId || ""}
                          onChange={(e) => setSelectedPayRateId(Number(e.target.value))}
                        >
                          {(payRates || []).map((r) => (
                            <option key={r.ID} value={r.ID}>
                              {`${r.Category || "No Category"} | Scale: ${r.PayScale} | Effective: ${
                                (r.EffectiveFrom || "").toString().slice(0, 10) || "-"
                              }`}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                    <div className="col-md-6 text-end">
                      <button
                        type="button"
                        className="button button-primary"
                        onClick={saveDetails}
                        disabled={saving || loadingRate || !selectedPayRate}
                      >
                        {saving ? "Saving..." : "Save Pay Rate Details"}
                      </button>
                    </div>
                  </div>

                  {selectedPayRate && (
                    <div className="mb-2 text-muted" style={{ fontSize: "13px" }}>
                      <strong>Pay Rate #{selectedPayRate.ID}</strong> | Category:{" "}
                      {selectedPayRate.Category || "-"} | Pay Scale: {selectedPayRate.PayScale}
                    </div>
                  )}

                  <div className="table-responsive">
                    <table className="table table-theme table-hover">
                      <thead>
                        <tr>
                          <th style={{ width: "60px" }}>#</th>
                          <th>Salary Type</th>
                          <th>Display Name</th>
                          <th>SType</th>
                          <th style={{ minWidth: "180px" }}>Amount</th>
                        </tr>
                      </thead>
                      <tbody>
                        {loadingRate ? (
                          <tr>
                            <td colSpan="5" className="text-center">
                              <Spinner animation="border" role="status" />
                            </td>
                          </tr>
                        ) : salaryTypes.length === 0 ? (
                          <tr>
                            <td colSpan="5" className="text-center">
                              No salary types found.
                            </td>
                          </tr>
                        ) : (
                          salaryTypes.map((st, idx) => (
                            <tr key={st.ID}>
                              <td>{idx + 1}</td>
                              <td>{st.Name}</td>
                              <td>{st.DisplayName || "-"}</td>
                              <td>{st.SType || "-"}</td>
                              <td>
                                <input
                                  type="number"
                                  step="0.01"
                                  className="form-control form-control-sm"
                                  value={amounts[st.ID] ?? ""}
                                  onChange={(e) => onAmountChange(st.ID, e.target.value)}
                                  disabled={saving || loadingRate}
                                />
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default ManagePayRateDetails;
